import { useSuspenseQuery } from '@tanstack/react-query';
import { useVirtualizer, type VirtualItem, type Virtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import type { NavigationScreenSpaceChangeRequestState } from '@gitbook/app-navigation';
import { DateShortRelative } from '@gitbook/react-uikit';
import { getScrollParent, IconSkeleton, TextSkeleton } from '@gitbook/spine-react';
import { getChangeRequestSubject } from '@gitbook/util-format';

import { useNavigation } from '../../navigation';
import {
    type APIResourceInput,
    apiQuery,
    apiResource,
    getAPIQueryKey,
    hashQueryKey,
    useAssertCurrentOrganizationId,
} from '../../state';
import { useSpace } from '../Space';
import { UserProfilePrettyName } from '../Users';
import {
    ChangeRequestActionsButton,
    type ChangeRequestActionsButtonProps,
} from './ChangeRequestActionsButton';
import { ChangeRequestsListEmptyState } from './ChangeRequestListEmptyState';
import { ChangeRequestStatusIcon } from './ChangeRequestStatus';
import type { ChangeRequestsListFilterState } from './ChangeRequestsListFilters';

export type ChangeRequestsListProps = {
    /** ID or number of the change request to highlight */
    selected?: NavigationScreenSpaceChangeRequestState | null;

    /** If provided, only change requests from the space will be displayed */
    spaceId?: string;

    /** Filters to apply to the list of change requests */
    filters: ChangeRequestsListFilterState;

    /**
     * Callback when filters change
     */
    onFiltersChange: (filters: ChangeRequestsListFilterState) => void;

    /**
     * Class name to apply to the list container.
     */
    className?: string;

    /**
     * Custom empty state to use.
     */
    emptyState?: React.ReactNode;
} & Pick<ChangeRequestListItemProps, 'onAction'>;

/**
 * List of change requests for an organization or space.
 */
export function ChangeRequestsList(props: ChangeRequestsListProps) {
    const { spaceId, filters, onFiltersChange, className, emptyState, onAction, selected } = props;

    const organizationId = useAssertCurrentOrganizationId();

    const deferredFilters = React.useDeferredValue(filters);
    const status = deferredFilters?.status ?? api.ChangeRequestStatus.Open;

    const listSpaceId = deferredFilters?.space || spaceId;
    const [limit] = React.useState(() => {
        return Math.min(Math.ceil(window.innerHeight / 45), 50);
    });
    const resource = listSpaceId
        ? apiResource('listChangeRequestsForSpace', {
              spaceId: listSpaceId,
              status,
              contributor: deferredFilters?.contributor,
              creator: deferredFilters?.creator,
              requestedReviewer: deferredFilters?.requestedReview,
              conversationsIssuesCluster: deferredFilters?.conversationsIssuesCluster,
              limit,
          })
        : apiResource('listChangeRequestsForOrganization', {
              organizationId,
              status: [status],
              contributor: deferredFilters?.contributor,
              creator: deferredFilters?.creator,
              requestedReviewer: deferredFilters?.requestedReview,
              conversationsIssuesCluster: deferredFilters?.conversationsIssuesCluster,
              limit,
          });
    const [isFetchingPage, startTransition] = React.useTransition();
    const queryKey = hashQueryKey(getAPIQueryKey(resource));
    const [pageState, setPageState] = React.useState<{
        page: string;
        key: string;
    } | null>(null);
    const { data } = useSuspenseQuery(
        apiQuery(paginateResource(resource, pageState?.key === queryKey ? pageState.page : null))
    );
    const nextPage = data.next?.page;
    const items = useListItems(queryKey, data.items);
    const fetchNextPage = React.useMemo(() => {
        return nextPage
            ? () => {
                  startTransition(() => {
                      setPageState({ page: nextPage, key: queryKey });
                  });
              }
            : undefined;
    }, [nextPage, queryKey]);

    if (!data) {
        return null;
    }

    if (items.length === 0 && data.items.length === 0) {
        return (
            emptyState ?? (
                <ChangeRequestsListEmptyState
                    filters={deferredFilters}
                    onFiltersChange={onFiltersChange}
                />
            )
        );
    }

    if (items.length === 0) {
        return null;
    }

    return (
        <div className={clsx('min-h-0 flex-1 overflow-y-auto', className)}>
            <ListRenderer
                items={items}
                renderItem={(virtualItem, changeRequest, virtualizer) => {
                    return (
                        <ChangeRequestListItem
                            key={virtualItem.key}
                            ref={virtualizer.measureElement}
                            index={virtualItem.index}
                            changeRequest={changeRequest}
                            inSpaceContext={Boolean(spaceId)}
                            onAction={onAction}
                            isSelected={Boolean(
                                selected &&
                                    (changeRequest.number === selected.changeRequest ||
                                        changeRequest.id === selected.changeRequest) &&
                                    changeRequest.space === selected.space
                            )}
                        />
                    );
                }}
                isStale={deferredFilters !== filters}
                isFetchingPage={isFetchingPage}
                onFetchNextPage={fetchNextPage}
            />
        </div>
    );
}

function paginateResource<T extends APIResourceInput>(resource: T, page: string | null): T {
    return resource.query || page
        ? {
              ...resource,
              query: {
                  ...resource?.query,
                  ...(page ? { page } : {}),
              },
          }
        : resource;
}

/**
 * Concatenate the list items.
 */
function useListItems<T extends { id: string }>(key: string, pageItems: T[]) {
    const [state, setState] = React.useState<{ key: string; items: T[] }>({
        key,
        items: pageItems,
    });

    React.useLayoutEffect(() => {
        setState((state) => {
            if (state.key === key) {
                return {
                    key,
                    items: [
                        // Deduplicate previous items.
                        ...state.items.filter((item) => !pageItems.some((i) => item.id === i.id)),
                        // Add new ones.
                        ...pageItems,
                    ],
                };
            }
            return { key, items: pageItems };
        });
    }, [key, pageItems]);

    return state.key === key ? state.items : pageItems;
}

function getChangeRequestTarget(props: {
    organizationId: string;
    mode?: 'edit';
    changeRequest: api.ChangeRequest;
}) {
    const { changeRequest, organizationId, mode } = props;
    return {
        screen: {
            type: 'space' as const,
            space: changeRequest.space,
            organization: organizationId ?? undefined,
            changeRequest: changeRequest.number,
            mode,
            sideSheet: undefined,
        },
    };
}

type ChangeRequestListItemProps = {
    changeRequest: api.ChangeRequest;
    index: number;
    inSpaceContext: boolean;
    onAction?: (changeRequest: api.ChangeRequest) => void;
    isSelected: boolean;
};

// Shared between component and skeleton
const ChangeRequestListItemClassNames = {
    container: clsx(
        'group @container flex items-center gap-4 whitespace-nowrap border-b px-4 py-3'
    ),
    inner: clsx('flex flex-1 flex-col gap-x-2 gap-y-0.5'),
    subject: clsx('flex items-start gap-2'),
    footer: clsx('flex items-center justify-between gap-1 pl-6 text-muted text-ui-small'),
};

const ChangeRequestListItem = React.forwardRef(function ChangeRequestListItem(
    props: ChangeRequestListItemProps,
    ref: React.ForwardedRef<HTMLAnchorElement>
) {
    const { changeRequest, inSpaceContext, index, onAction, isSelected } = props;
    const { navigate, getNavigationURL } = useNavigation();
    const organizationId = useAssertCurrentOrganizationId();
    const target = getChangeRequestTarget({ organizationId, changeRequest });
    const actionButtonProps = { changeRequest };
    return (
        <a
            ref={ref}
            data-index={index}
            href={getNavigationURL(target)}
            onClick={(event) => {
                if (event.isDefaultPrevented()) {
                    return;
                }

                // if shift or ctrl are pressed, use the
                // default browser behaviour and open the link in a new tab.
                if (event.metaKey || event.ctrlKey) {
                    return;
                }

                event.preventDefault();
                if (onAction) {
                    onAction(changeRequest);
                } else {
                    navigate(target);
                }
            }}
            className={clsx(
                ChangeRequestListItemClassNames.container,
                'focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-inset',
                isSelected
                    ? 'bg-[#0000000F] dark:bg-[#FFFFFF12]'
                    : 'hover:bg-[#00000006] dark:bg-[#FFFFFF09]'
            )}
        >
            <div className={ChangeRequestListItemClassNames.inner}>
                <div className={ChangeRequestListItemClassNames.subject}>
                    <ChangeRequestStatusIcon
                        status={changeRequest.status}
                        colored
                        className="my-0.5 size-4 shrink-0"
                    />
                    <span className="flex-1 whitespace-pre-wrap font-medium">
                        {getChangeRequestSubject(changeRequest)}
                    </span>
                    <React.Suspense
                        fallback={<ActionsButton {...actionButtonProps} space={null} />}
                    >
                        <ActionsButtonWithSpace {...actionButtonProps} />
                    </React.Suspense>
                </div>
                <div className={ChangeRequestListItemClassNames.footer}>
                    <div className="flex flex-1 items-center gap-1 truncate">
                        <span className="shrink-0 text-muted">#{changeRequest.number}</span>
                        {inSpaceContext ? null : (
                            <React.Suspense fallback={null}>
                                <ChangeRequestSpace changeRequest={changeRequest} />
                                <span>—</span>
                            </React.Suspense>
                        )}
                        Created by <UserProfilePrettyName user={changeRequest.createdBy} />
                    </div>
                    <DateShortRelative
                        className="shrink-0 text-muted"
                        value={changeRequest.createdAt}
                        onlyDate={false}
                    />
                </div>
            </div>
        </a>
    );
});

export function ChangeRequestListItemSkeleton() {
    return (
        <div className={ChangeRequestListItemClassNames.container}>
            <div className={ChangeRequestListItemClassNames.inner}>
                <div className={ChangeRequestListItemClassNames.subject}>
                    <IconSkeleton className="size-4" />
                    <TextSkeleton>This is my subject</TextSkeleton>
                </div>
                <div className={ChangeRequestListItemClassNames.footer}>
                    <TextSkeleton>#45 - Created by James Bond</TextSkeleton>
                    <TextSkeleton>28d ago</TextSkeleton>
                </div>
            </div>
        </div>
    );
}

function ActionsButtonWithSpace(props: Omit<ActionsButtonProps, 'space'>) {
    const { changeRequest } = props;
    const organizationId = useAssertCurrentOrganizationId();
    const space = useSpace({ organizationId, spaceId: changeRequest.space });
    return <ActionsButton space={space} {...props} />;
}

type ActionsButtonProps = Pick<ChangeRequestActionsButtonProps, 'space' | 'changeRequest'>;

function ActionsButton(props: ActionsButtonProps) {
    return (
        <ChangeRequestActionsButton
            className="opacity-0 focus:opacity-100 group-hover:opacity-100"
            preventFocusOnPress
            size="small"
            kind="nested"
            {...props}
        />
    );
}

type ListItem = { id: string };

type ListRendererProps<T extends ListItem> = {
    items: T[];
    isStale: boolean;
    isFetchingPage: boolean;
    onFetchNextPage: (() => void) | undefined;
    renderItem: (
        virtualItem: VirtualItem,
        item: T,
        virtualizer: Virtualizer<Element, Element>
    ) => React.ReactNode;
};

function ListRenderer<T extends ListItem>(props: ListRendererProps<T>) {
    const { items, isStale, onFetchNextPage, isFetchingPage, renderItem } = props;
    const count = items.length;
    const contentRef = React.useRef<HTMLDivElement>(null);
    const virtualizer = useVirtualizer({
        count: onFetchNextPage ? count + 1 : count,
        getScrollElement: () => (contentRef.current ? getScrollParent(contentRef.current) : null),
        estimateSize: (index) => (items[index] ? 67 : 52),
        paddingEnd: 20,
        getItemKey: (index) => {
            return items[index]?.id ?? `loader-${index}`;
        },
    });
    const virtualItems = virtualizer.getVirtualItems();
    const lastItem = virtualItems[virtualItems.length - 1];
    React.useEffect(() => {
        if (lastItem && lastItem.index === count && !isFetchingPage && onFetchNextPage) {
            onFetchNextPage();
        }
    }, [lastItem, count, isFetchingPage, onFetchNextPage]);

    return (
        <div
            ref={contentRef}
            className={clsx('relative', isStale && 'animate-pulse [animation-duration:1.5s]')}
            style={{
                height: virtualizer.getTotalSize(),
            }}
        >
            <div
                className="flex min-w-full flex-col"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
                }}
            >
                {virtualItems.map((virtualItem) => {
                    const item = items[virtualItem.index];
                    if (!item) {
                        return (
                            <div
                                key={virtualItem.key}
                                ref={virtualizer.measureElement}
                                data-index={virtualItem.index}
                                className="py-4 text-center text-muted"
                            >
                                Fetching more items…
                            </div>
                        );
                    }
                    return renderItem(virtualItem, item, virtualizer);
                })}
            </div>
        </div>
    );
}

function ChangeRequestSpace(props: { changeRequest: api.ChangeRequest }) {
    const { changeRequest } = props;
    const organizationId = useAssertCurrentOrganizationId();
    const space = useSpace({ organizationId, spaceId: changeRequest.space });
    if (space) {
        return <>{space.title}</>;
    }
    return <>Deleted space</>;
}
